import React, { useState, useEffect } from 'react';
import { X, Upload, Download, AlertCircle, CheckCircle } from 'lucide-react';
import { modelImportService } from '../services/modelImportService';
import '../styles/Buttons.css';

interface ModelImportModalProps {
  isOpen: boolean;
  workspaceId: string;
  projectId: string;
  builderId: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface ParsedRow {
  rowNumber: number;
  data: any;
  errors: string[];
}

const ModelImportModal: React.FC<ModelImportModalProps> = ({
  isOpen,
  workspaceId,
  projectId,
  builderId,
  onClose,
  onSuccess
}) => {
  const [step, setStep] = useState<'upload' | 'preview' | 'importing' | 'complete'>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<any>(null);
  const [editedRows, setEditedRows] = useState<ParsedRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importResult, setImportResult] = useState<any>(null);

  useEffect(() => {
    if (parsedData && parsedData.rows) {
      setEditedRows(parsedData.rows);
    }
  }, [parsedData]);

  const handleDownloadTemplate = async (format: 'xlsx' | 'csv') => {
    try {
      setLoading(true);
      const blob = await modelImportService.downloadTemplate(workspaceId, format);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `model_import_template.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to download template');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError('');
    }
  };

  const handleParseFile = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const result = await modelImportService.parseFile(workspaceId, selectedFile);
      setParsedData(result);
      setStep('preview');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to parse file');
    } finally {
      setLoading(false);
    }
  };

  const handleCellEdit = (rowIndex: number, field: string, value: any) => {
    const newRows = [...editedRows];
    newRows[rowIndex].data[field] = value;
    setEditedRows(newRows);
  };

  const handleImport = async () => {
    try {
      setLoading(true);
      setError('');
      setStep('importing');

      // Extract only data from rows (remove rowNumber and errors)
      const rowsData = editedRows.map(row => row.data);

      const result = await modelImportService.bulkCreate(workspaceId, projectId, builderId, rowsData);
      setImportResult(result);
      setStep('complete');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to import models');
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    onSuccess();
    onClose();
  };

  if (!isOpen) return null;

  const renderUploadStep = () => (
    <>
      <div className="modal-body" style={{ padding: '24px' }}>
        {/* Template Download Section */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
            Step 1: Download Template
          </h3>
          <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
            Download the template file, fill in your model data, and upload it back.
          </p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => handleDownloadTemplate('xlsx')}
              disabled={loading}
              className="btn btn-secondary btn-md"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Download size={16} />
              Download Excel Template
            </button>
            <button
              onClick={() => handleDownloadTemplate('csv')}
              disabled={loading}
              className="btn btn-secondary btn-md"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Download size={16} />
              Download CSV Template
            </button>
          </div>
        </div>

        {/* File Upload Section */}
        <div>
          <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
            Step 2: Upload Filled Template
          </h3>
          <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
            Select the filled Excel or CSV file to import models.
          </p>

          <div
            style={{
              border: '2px dashed #D1D5DB',
              borderRadius: '8px',
              padding: '32px',
              textAlign: 'center',
              backgroundColor: '#F9FAFB',
              cursor: 'pointer'
            }}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <Upload size={48} style={{ color: '#9CA3AF', margin: '0 auto 16px' }} />
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '4px' }}>
              {selectedFile ? selectedFile.name : 'Click to upload or drag and drop'}
            </p>
            <p style={{ fontSize: '12px', color: '#6B7280' }}>
              Excel (.xlsx, .xls) or CSV (.csv) files
            </p>
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        {error && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#FEE2E2',
            color: '#991B1B',
            borderRadius: '6px',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AlertCircle size={16} />
            {error}
          </div>
        )}
      </div>

      <div className="modal-footer" style={{ padding: '16px 24px', borderTop: '1px solid #E5E7EB' }}>
        <button
          onClick={onClose}
          disabled={loading}
          className="btn btn-secondary btn-md"
        >
          Cancel
        </button>
        <button
          onClick={handleParseFile}
          disabled={!selectedFile || loading}
          className="btn btn-primary btn-md"
        >
          {loading ? 'Parsing...' : 'Next'}
        </button>
      </div>
    </>
  );

  const renderPreviewStep = () => (
    <>
      <div className="modal-body" style={{ padding: '24px', maxHeight: 'calc(90vh - 200px)', overflow: 'auto' }}>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>
              Preview & Edit Data
            </h3>
            <p style={{ fontSize: '14px', color: '#6B7280' }}>
              Review the imported data. You can edit any cell before importing.
              {parsedData?.errorCount > 0 && (
                <span style={{ color: '#DC2626', marginLeft: '8px' }}>
                  {parsedData.errorCount} row(s) with errors
                </span>
              )}
            </p>
          </div>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
            {parsedData?.totalRows || 0} rows
          </div>
        </div>

        {/* Excel-like Data Grid */}
        <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: '6px' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '13px',
            backgroundColor: 'white'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#F3F4F6' }}>
                <th style={{ padding: '10px', borderRight: '1px solid #E5E7EB', borderBottom: '1px solid #E5E7EB', fontWeight: '600', textAlign: 'left', minWidth: '60px' }}>Row</th>
                {parsedData?.headers?.map((header: any) => (
                  <th key={header.key} style={{
                    padding: '10px',
                    borderRight: '1px solid #E5E7EB',
                    borderBottom: '1px solid #E5E7EB',
                    fontWeight: '600',
                    textAlign: 'left',
                    minWidth: '150px',
                    whiteSpace: 'nowrap'
                  }}>
                    {header.header}
                    {header.required && <span style={{ color: '#DC2626', marginLeft: '4px' }}>*</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {editedRows.map((row, rowIndex) => (
                <tr key={rowIndex} style={{
                  backgroundColor: row.errors.length > 0 ? '#FEF2F2' : 'white'
                }}>
                  <td style={{
                    padding: '8px 10px',
                    borderRight: '1px solid #E5E7EB',
                    borderBottom: '1px solid #E5E7EB',
                    fontWeight: '600',
                    color: row.errors.length > 0 ? '#DC2626' : '#6B7280'
                  }}>
                    {row.rowNumber}
                  </td>
                  {parsedData?.headers?.map((header: any) => (
                    <td key={header.key} style={{
                      padding: '4px',
                      borderRight: '1px solid #E5E7EB',
                      borderBottom: '1px solid #E5E7EB'
                    }}>
                      <input
                        type="text"
                        value={row.data[header.key] || ''}
                        onChange={(e) => handleCellEdit(rowIndex, header.key, e.target.value)}
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          border: '1px solid transparent',
                          borderRadius: '4px',
                          fontSize: '13px',
                          outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.border = '1px solid #48182F'}
                        onBlur={(e) => e.target.style.border = '1px solid transparent'}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Error Summary */}
        {parsedData?.errors && parsedData.errors.length > 0 && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FCA5A5',
            borderRadius: '6px'
          }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#DC2626', marginBottom: '8px' }}>
              Validation Errors
            </h4>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#991B1B' }}>
              {parsedData.errors.map((err: any, index: number) => (
                <li key={index}>
                  Row {err.row}: {err.errors.join(', ')}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="modal-footer" style={{ padding: '16px 24px', borderTop: '1px solid #E5E7EB' }}>
        <button
          onClick={() => {
            setStep('upload');
            setParsedData(null);
            setEditedRows([]);
          }}
          disabled={loading}
          className="btn btn-secondary btn-md"
        >
          Back
        </button>
        <button
          onClick={handleImport}
          disabled={loading || (parsedData?.errorCount > 0)}
          className="btn btn-primary btn-md"
        >
          Import {parsedData?.totalRows || 0} Models
        </button>
      </div>
    </>
  );

  const renderImportingStep = () => (
    <div className="modal-body" style={{ padding: '48px 24px', textAlign: 'center' }}>
      <div className="builders-loading-spinner" style={{ width: '48px', height: '48px', margin: '0 auto 24px' }} />
      <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
        Importing Models...
      </h3>
      <p style={{ fontSize: '14px', color: '#6B7280' }}>
        Please wait while we create your models. This may take a moment.
      </p>
    </div>
  );

  const renderCompleteStep = () => (
    <>
      <div className="modal-body" style={{ padding: '48px 24px', textAlign: 'center' }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          backgroundColor: '#DEF7EC',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px'
        }}>
          <CheckCircle size={32} style={{ color: '#10B981' }} />
        </div>
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
          Import Complete!
        </h3>
        <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '24px' }}>
          Successfully created {importResult?.created || 0} models
          {importResult?.failed > 0 && ` (${importResult.failed} failed)`}
        </p>

        {importResult?.errors && importResult.errors.length > 0 && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FCA5A5',
            borderRadius: '6px',
            textAlign: 'left'
          }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#DC2626', marginBottom: '8px' }}>
              Failed to create {importResult.failed} model(s):
            </h4>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#991B1B' }}>
              {importResult.errors.map((err: any, index: number) => (
                <li key={index}>
                  {err.name}: {err.error}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="modal-footer" style={{ padding: '16px 24px', borderTop: '1px solid #E5E7EB' }}>
        <button
          onClick={handleComplete}
          className="btn btn-primary btn-md"
        >
          Done
        </button>
      </div>
    </>
  );

  return (
    <div className="modal-overlay" style={{ zIndex: 1000 }}>
      <div
        className="modal-content"
        style={{
          maxWidth: step === 'preview' ? '90vw' : '600px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div className="modal-header" style={{ padding: '20px 24px', borderBottom: '1px solid #E5E7EB' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>Import Models</h2>
          <button className="modal-close-btn" onClick={onClose} disabled={loading}>
            <X size={24} />
          </button>
        </div>

        {step === 'upload' && renderUploadStep()}
        {step === 'preview' && renderPreviewStep()}
        {step === 'importing' && renderImportingStep()}
        {step === 'complete' && renderCompleteStep()}
      </div>
    </div>
  );
};

export default ModelImportModal;
